/**
 * EventDrop Pro ✦ — Booth Sessions studio dashboard.
 *
 * Craig's professional memory studio. NOT enterprise SaaS, NOT a
 * control panel, NOT an operator portal. Each session below is a
 * preserved night running through Booth OS — same atmosphere, same
 * gallery quality, same pairing elegance as any commissioned night.
 *
 * Identity (per Craig's 2026-05-15 brief):
 *   • Darker walnut tones than the host dashboard
 *   • Restrained gold (C29D52 vs the host's warmer D4AA52)
 *   • Larger typography spacing — reads as a memory atelier
 *   • Emotional copy throughout — never "campaign", "workspace",
 *     "operator portal", "tenant", "metrics", or admin language
 *
 * Routing
 *   • Unauthenticated → /pro/login (NOT /login — dedicated entry)
 *   • Authenticated non-operator → calm "This room is not open
 *     tonight ✦" message. Never "Unauthorized", never "403".
 *   • Operator → studio ledger
 *
 * Three actions per session (per the brief):
 *   • Pair Booth ✦   → /event/:slug (Setup tab carries the pairing)
 *   • Preview Booth ✦ → /booth-os/:event_id (kiosk, new tab)
 *   • Open Gallery ✦ → /event/:slug/memory-wall (the living gallery)
 *
 * Segmented filter: Upcoming · Recent · Archived. Computed
 * client-side from event_date (held_on) and created_at — no
 * backend roundtrip per filter change. Simple toggle, no tables.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PRO_PALETTE } from './ProLoginPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_ATMOSPHERES = [
  { id: 'rustic_evening', label: 'Rustic Evening' },
  { id: 'botanical_wedding', label: 'Botanical Wedding' },
  { id: 'glam_celebration', label: 'Glam Celebration' },
  { id: 'elegant_ivory', label: 'Elegant Ivory' },
  { id: 'midnight_gold', label: 'Midnight Gold' },
];

const THEME_LABEL = Object.fromEntries(
  SESSION_ATMOSPHERES.map((t) => [t.id, t.label]),
);

const fmtHeldOn = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

// ─── Time/state helpers ─────────────────────────────────────────
// "Upcoming"  → event_date is today or in the future (or null —
//               a venue showcase / always-on session)
// "Recent"    → event_date within the last 60 days
// "Archived"  → event_date older than 60 days
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
function categoriseSession(session) {
  const iso = session.event_date;
  if (!iso) return 'upcoming';  // open-ended sessions live in Upcoming
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'upcoming';
  const now = Date.now();
  if (t >= now - 24 * 60 * 60 * 1000) return 'upcoming';   // today or future
  if (t >= now - SIXTY_DAYS_MS) return 'recent';
  return 'archived';
}

// One quiet status line per session — atmospheric, never metric.
function statusLine(session) {
  const iso = session.event_date;
  if (!iso) return 'Held in reserve · ready when the room is.';
  const t = new Date(iso).getTime();
  const now = Date.now();
  if (Number.isNaN(t)) return 'Held in reserve · ready when the room is.';
  const dayMs = 24 * 60 * 60 * 1000;
  // Tonight (within ±18h of event_date) — the most emotional state.
  if (Math.abs(t - now) < 18 * 60 * 60 * 1000) return 'The room is alive ✦';
  if (t > now && t - now < dayMs) return 'The room gathers tomorrow ✦';
  if (t > now) {
    const days = Math.round((t - now) / dayMs);
    return `Approaches quietly · ${days} ${days === 1 ? 'day' : 'days'} away`;
  }
  // Past
  const days = Math.round((now - t) / dayMs);
  if (days <= 3) return 'Memories are settling softly ✦';
  if (days <= 30) return `${days} ${days === 1 ? 'day' : 'days'} preserved ·  the archive is breathing`;
  if (days <= 365) {
    const months = Math.max(1, Math.round(days / 30));
    return `Held in the archive · ${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  const years = Math.max(1, Math.round(days / 365));
  return `Long preserved · ${years} ${years === 1 ? 'year' : 'years'} on`;
}


// ─── Inline create form ─────────────────────────────────────────
const CreateSessionPanel = ({ onCreated, onClose }) => {
  const [name, setName] = useState('');
  const [themeId, setThemeId] = useState('rustic_evening');
  const [heldOn, setHeldOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('A session needs a name to be remembered by.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/pro/sessions`, {
        name: name.trim(),
        theme_id: themeId,
        held_on: heldOn || null,
      });
      onCreated(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'The studio could not compose the session.');
    } finally {
      setBusy(false);
    }
  };

  const fieldLabel = (s) => (
    <div style={{
      fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
      fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
      color: PRO_PALETTE.goldSoft, marginBottom: 8,
    }}>{s}</div>
  );

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    background: PRO_PALETTE.bgRaised,
    border: `1px solid ${PRO_PALETTE.goldHair}`,
    borderRadius: 3,
    fontFamily: PRO_PALETTE.serif, fontSize: 16, fontStyle: 'italic',
    color: PRO_PALETTE.ink, outline: 'none',
  };

  return (
    <div
      data-testid="pro-create-session-panel"
      style={{
        padding: '32px 36px',
        background: PRO_PALETTE.panel,
        border: `1px solid ${PRO_PALETTE.goldHair}`,
        borderRadius: 4,
        marginBottom: 28,
      }}
    >
      <div style={{
        fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
        fontSize: 10.5, letterSpacing: '0.32em', textTransform: 'uppercase',
        color: PRO_PALETTE.goldSoft, marginBottom: 6,
      }}>Compose a Booth Session ✦</div>
      <div style={{
        marginBottom: 24,
        fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
        fontSize: 14, color: PRO_PALETTE.inkMuted, lineHeight: 1.55,
      }}>
        A preserved night without the planning layer — atmosphere, gallery, pairing and Booth OS ready the moment it is named.
      </div>

      <form onSubmit={submit} data-testid="pro-create-session-form">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 22,
        }}>
          <label style={{ display: 'block', gridColumn: '1 / -1' }}>
            {fieldLabel('Session name')}
            <input
              type="text" value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Emma & Jake ✦"
              autoFocus maxLength={80}
              data-testid="pro-create-session-name"
              style={inputStyle}
            />
          </label>

          <label style={{ display: 'block' }}>
            {fieldLabel('Atmosphere')}
            <select
              value={themeId}
              onChange={(e) => setThemeId(e.target.value)}
              data-testid="pro-create-session-theme"
              style={{ ...inputStyle, cursor: 'pointer', fontSize: 15 }}
            >
              {SESSION_ATMOSPHERES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'block' }}>
            {fieldLabel('Held on · optional')}
            <input
              type="date" value={heldOn}
              onChange={(e) => setHeldOn(e.target.value)}
              data-testid="pro-create-session-held-on"
              style={{
                ...inputStyle, fontSize: 15,
                color: heldOn ? PRO_PALETTE.ink : PRO_PALETTE.inkFaint,
              }}
            />
          </label>
        </div>

        {error && (
          <div
            data-testid="pro-create-session-error"
            style={{
              marginTop: 18,
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 13, color: '#E7B79C',
            }}
          >✦ {error}</div>
        )}

        <div style={{
          marginTop: 24, display: 'flex', gap: 12,
          justifyContent: 'flex-end', alignItems: 'center',
        }}>
          <button
            type="button" onClick={onClose}
            data-testid="pro-create-session-cancel"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 13.5, color: PRO_PALETTE.inkMuted,
              padding: '10px 14px',
            }}
          >Set aside</button>
          <button
            type="submit" disabled={busy}
            data-testid="pro-create-session-submit"
            style={{
              padding: '11px 22px',
              background: busy ? 'rgba(194,157,82,0.18)' : PRO_PALETTE.gold,
              border: `1px solid ${PRO_PALETTE.gold}`,
              borderRadius: 3,
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 14, letterSpacing: '0.04em',
              color: busy ? PRO_PALETTE.inkMuted : '#1A1208',
              cursor: busy ? 'wait' : 'pointer',
            }}
          >{busy ? 'Composing…' : 'Compose the session ✦'}</button>
        </div>
      </form>
    </div>
  );
};


// ─── Session row ────────────────────────────────────────────────
const SessionRow = ({ session }) => {
  const heldOn = fmtHeldOn(session.event_date);
  const atmosphereLabel = THEME_LABEL[session.theme] || 'Rustic Evening';
  const status = statusLine(session);
  const isAlive = status === 'The room is alive ✦';

  const linkStyle = {
    flex: 1, textAlign: 'center',
    padding: '10px 14px',
    fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
    fontSize: 13.5, letterSpacing: '0.01em',
    color: PRO_PALETTE.goldSoft,
    background: 'transparent',
    border: `1px solid ${PRO_PALETTE.goldHair}`,
    borderRadius: 3,
    textDecoration: 'none',
    transition: 'border-color 220ms ease, color 220ms ease, background 220ms ease',
  };

  return (
    <div
      data-testid={`pro-session-${session.slug}`}
      style={{
        padding: '28px 32px',
        background: PRO_PALETTE.panel,
        border: `1px solid ${PRO_PALETTE.goldHair}`,
        borderRadius: 4,
        marginBottom: 16,
        // Live nights get a faint gold pulse — never a loud animation.
        boxShadow: isAlive ? `0 0 0 1px rgba(194,157,82,0.32) inset` : 'none',
      }}
    >
      {/* Eyebrow */}
      <div style={{
        fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
        fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: PRO_PALETTE.goldSoft, marginBottom: 8,
      }}>
        Booth Session ✦
        {heldOn && (
          <>
            <span style={{ margin: '0 8px', opacity: 0.45 }}>·</span>
            {`Held on ${heldOn}`}
          </>
        )}
      </div>

      {/* Title */}
      <div
        data-testid={`pro-session-${session.slug}-title`}
        style={{
          fontFamily: PRO_PALETTE.serif, fontWeight: 500,
          fontSize: 'clamp(24px, 2.7vw, 30px)',
          color: PRO_PALETTE.ink,
          letterSpacing: '-0.012em', lineHeight: 1.12,
        }}
      >
        {session.event_name}
      </div>

      {/* Atmosphere + status (the quiet line) */}
      <div style={{
        marginTop: 10,
        fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
        fontSize: 14, color: PRO_PALETTE.inkMuted, lineHeight: 1.55,
        letterSpacing: '0.005em',
      }}>
        {atmosphereLabel} atmosphere
      </div>
      <div
        data-testid={`pro-session-${session.slug}-status`}
        style={{
          marginTop: 4,
          fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
          fontSize: 13, color: isAlive ? PRO_PALETTE.gold : PRO_PALETTE.inkFaint,
          letterSpacing: '0.005em',
        }}
      >
        {status}
      </div>

      {/* Three actions — pair, preview, gallery. */}
      <div style={{
        marginTop: 22,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
      }}>
        <Link
          to={`/event/${session.slug}?focus=pairing`}
          data-testid={`pro-session-${session.slug}-pair`}
          style={linkStyle}
        >Pair Booth ✦</Link>
        <a
          href={`/booth-os/${session.event_id}`}
          target="_blank" rel="noopener noreferrer"
          data-testid={`pro-session-${session.slug}-preview`}
          style={linkStyle}
        >Preview Booth ✦</a>
        <Link
          to={`/event/${session.slug}/memory-wall`}
          data-testid={`pro-session-${session.slug}-gallery`}
          style={linkStyle}
        >Open Gallery ✦</Link>
      </div>
    </div>
  );
};


// ─── Segmented filter ───────────────────────────────────────────
const FilterTabs = ({ counts, active, onChange }) => {
  const tabs = [
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'recent',   label: 'Recent' },
    { id: 'archived', label: 'Archived' },
  ];
  return (
    <div
      data-testid="pro-filter-tabs"
      role="tablist"
      style={{
        display: 'inline-flex',
        gap: 0,
        border: `1px solid ${PRO_PALETTE.goldHair}`,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {tabs.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            data-testid={`pro-filter-${t.id}`}
            style={{
              padding: '9px 18px',
              background: isActive ? 'rgba(194,157,82,0.14)' : 'transparent',
              border: 'none',
              borderRight: t.id !== 'archived' ? `1px solid ${PRO_PALETTE.goldHair}` : 'none',
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 13, letterSpacing: '0.04em',
              color: isActive ? PRO_PALETTE.ink : PRO_PALETTE.inkMuted,
              cursor: 'pointer',
              transition: 'background 200ms ease, color 200ms ease',
            }}
          >
            {t.label}
            <span style={{
              marginLeft: 8, fontSize: 11, color: PRO_PALETTE.inkFaint,
            }}>
              {counts[t.id] ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
};


// ─── Page ───────────────────────────────────────────────────────
export default function ProSessionsPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('upcoming');

  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pro/sessions`);
      setSessions(r.data || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        navigate('/pro/login?return=/pro', { replace: true });
        return;
      }
      if (status === 404) {
        setError('not_pro');
        return;
      }
      setError('load_failed');
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/pro/login?return=/pro', { replace: true });
      return;
    }
    loadSessions();
  }, [user, authLoading, loadSessions, navigate]);

  // Categorise + count once per sessions update.
  const { categorised, counts } = useMemo(() => {
    const cat = { upcoming: [], recent: [], archived: [] };
    if (sessions) {
      for (const s of sessions) {
        const c = categoriseSession(s);
        cat[c].push(s);
      }
    }
    return {
      categorised: cat,
      counts: {
        upcoming: cat.upcoming.length,
        recent: cat.recent.length,
        archived: cat.archived.length,
      },
    };
  }, [sessions]);

  // ─── Non-Pro calm state ───────────────────────────────────────
  if (error === 'not_pro') {
    return (
      <div
        data-testid="pro-not-open"
        style={{
          minHeight: '100vh', background: PRO_PALETTE.bg,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: 48, textAlign: 'center',
        }}
      >
        <div style={{
          fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: PRO_PALETTE.goldSoft, marginBottom: 16,
        }}>EventDrop Pro ✦</div>
        <h1 style={{
          margin: 0, marginBottom: 14,
          fontFamily: PRO_PALETTE.serif, fontWeight: 500,
          fontSize: 'clamp(28px, 4.5vw, 40px)',
          color: PRO_PALETTE.ink, lineHeight: 1.1,
        }}>This room is not open tonight ✦</h1>
        <p style={{
          margin: 0, maxWidth: 460,
          fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
          fontSize: 14, color: PRO_PALETTE.inkMuted, lineHeight: 1.55,
        }}>
          The studio is reserved for the operators who tend it. Your own nights are kept in the regular dashboard, where they will be ready when you are.
        </p>
        <div style={{ marginTop: 28 }}>
          <Link
            to="/dashboard"
            data-testid="pro-return-to-host"
            style={{
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 14, color: PRO_PALETTE.gold,
              textDecoration: 'underline',
            }}
          >Return to your nights</Link>
        </div>
      </div>
    );
  }

  const visible = categorised[filter];

  return (
    <div
      data-testid="pro-sessions-page"
      style={{
        minHeight: '100vh', background: PRO_PALETTE.bg,
        backgroundImage: `
          radial-gradient(ellipse 80% 50% at 50% -10%, rgba(194,157,82,0.05), transparent 60%)
        `,
      }}
    >
      <div style={{
        maxWidth: 1080, margin: '0 auto',
        padding: 'clamp(40px, 6vw, 72px) clamp(20px, 4vw, 48px)',
      }}>
        {/* ── HEADER ─────────────────────────────────────────── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-end', gap: 28, flexWrap: 'wrap',
          marginBottom: 36,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: PRO_PALETTE.goldSoft, marginBottom: 10,
            }}>EventDrop Pro ✦</div>
            <h1
              data-testid="pro-sessions-title"
              style={{
                margin: 0,
                fontFamily: PRO_PALETTE.serif, fontWeight: 500,
                fontSize: 'clamp(34px, 4.5vw, 48px)',
                color: PRO_PALETTE.ink,
                letterSpacing: '-0.014em', lineHeight: 1.05,
              }}
            >The Studio</h1>
            <div style={{
              marginTop: 14,
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 14.5, color: PRO_PALETTE.inkMuted,
              lineHeight: 1.55, maxWidth: 560,
            }}>
              A ledger of preserved nights — each one running through Booth OS. Atmosphere, gallery and pairing are held the same as any commissioned night.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setCreateOpen((v) => !v)}
              data-testid="pro-open-create"
              style={{
                padding: '11px 22px',
                background: createOpen ? 'transparent' : PRO_PALETTE.gold,
                border: `1px solid ${createOpen ? PRO_PALETTE.goldHair : PRO_PALETTE.gold}`,
                borderRadius: 3,
                fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
                fontSize: 14, letterSpacing: '0.04em',
                color: createOpen ? PRO_PALETTE.inkMuted : '#1A1208',
                cursor: 'pointer',
              }}
            >
              {createOpen ? 'Set aside' : 'Compose a session ✦'}
            </button>
            <Link
              to="/dashboard"
              data-testid="pro-back-to-host"
              style={{
                fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
                fontSize: 12.5, color: PRO_PALETTE.inkFaint,
                textDecoration: 'underline', textDecorationColor: PRO_PALETTE.goldHair,
                textUnderlineOffset: 4,
              }}
            >Your nights</Link>
            <button
              type="button"
              onClick={async () => { await logout(); navigate('/pro/login', { replace: true }); }}
              data-testid="pro-logout"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
                fontSize: 12.5, color: PRO_PALETTE.inkFaint, padding: 0,
                textDecoration: 'underline', textDecorationColor: PRO_PALETTE.goldHair,
                textUnderlineOffset: 4,
              }}
            >Close the studio</button>
          </div>
        </div>

        {/* ── DIVIDER ────────────────────────────────────────── */}
        <div style={{
          height: 1, background: PRO_PALETTE.goldHair, marginBottom: 28,
        }} />

        {/* ── CREATE FORM ────────────────────────────────────── */}
        {createOpen && (
          <CreateSessionPanel
            onClose={() => setCreateOpen(false)}
            onCreated={(s) => {
              setSessions((prev) => [s, ...(prev || [])]);
              setCreateOpen(false);
              // If the new session belongs to a different filter
              // bucket than the active one, switch so the operator
              // sees it land. Default to upcoming for new sessions
              // (most have either no date or a future date).
              const c = categoriseSession(s);
              if (c !== filter) setFilter(c);
            }}
          />
        )}

        {/* ── FILTER + LEDGER ────────────────────────────────── */}
        {sessions !== null && (
          <div style={{ marginBottom: 22 }}>
            <FilterTabs counts={counts} active={filter} onChange={setFilter} />
          </div>
        )}

        {sessions === null && (
          <div data-testid="pro-sessions-loading" style={{
            padding: '60px 0', textAlign: 'center',
            fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
            fontSize: 14, color: PRO_PALETTE.inkFaint,
          }}>Drawing the ledger…</div>
        )}

        {sessions !== null && visible.length === 0 && !createOpen && (
          <div
            data-testid="pro-sessions-empty"
            style={{
              padding: '54px 24px', textAlign: 'center',
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 15, color: PRO_PALETTE.inkMuted,
              maxWidth: 540, margin: '0 auto', lineHeight: 1.55,
            }}
          >
            {filter === 'upcoming' && 'No sessions ahead. The studio is quiet — compose one above.'}
            {filter === 'recent'   && 'Nothing recent. The freshly-held nights have moved on.'}
            {filter === 'archived' && 'The archive is empty. Older nights will settle here in time.'}
          </div>
        )}

        {sessions && visible.length > 0 && (
          <div data-testid="pro-sessions-ledger">
            {visible.map((s) => (
              <SessionRow key={s.event_id} session={s} />
            ))}
          </div>
        )}

        {error === 'load_failed' && (
          <div
            data-testid="pro-sessions-error"
            style={{
              marginTop: 40, padding: '20px 24px',
              border: `1px solid ${PRO_PALETTE.goldHair}`,
              borderRadius: 3, background: PRO_PALETTE.bgRaised,
              fontFamily: PRO_PALETTE.serif, fontStyle: 'italic',
              fontSize: 14, color: PRO_PALETTE.inkMid,
            }}
          >
            ✦ The ledger could not load just now. <button
              type="button"
              onClick={() => { setError(null); loadSessions(); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: PRO_PALETTE.gold, fontFamily: 'inherit', fontStyle: 'inherit',
                fontSize: 'inherit', textDecoration: 'underline',
                marginLeft: 6,
              }}
            >Try again</button>
          </div>
        )}

        <div style={{ height: 80 }} />
      </div>
    </div>
  );
}
